import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/axios';
import { FEATURES, permissionKey, type FeatureKey, type PermissionAction } from '../lib/permissions';

export interface SessionRole {
  id: string;
  slug: string;
  name: string;
}

interface SessionPermissionsResponse {
  data: { role: SessionRole | null; permissions: string[] };
}

export const PERMISSIONS_QUERY_KEY = ['auth', 'me', 'permissions'] as const;

async function fetchSessionPermissions() {
  const { data } = await api.get<SessionPermissionsResponse>('/public/auth/me/permissions');
  return data.data;
}

/**
 * Permissões efetivas da sessão, carregadas uma vez após o login.
 *
 * `staleTime: Infinity` porque a matriz muda raramente e sempre por ação
 * administrativa: recarregar a cada foco de janela seria custo sem ganho. Uma
 * alteração feita pelo Administrador vale no backend imediatamente (o guard
 * consulta a matriz a cada request); o que espera o próximo login é apenas o
 * que a interface *mostra*.
 */
export function usePermissions() {
  const { data, isLoading, isError } = useQuery({
    queryKey: PERMISSIONS_QUERY_KEY,
    queryFn: fetchSessionPermissions,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  const granted = new Set(data?.permissions ?? []);

  /**
   * Enquanto carrega, nada é permitido. Renderizar um botão antes de saber a
   * resposta e escondê-lo depois é pior que esperar: o usuário clica no que
   * vai virar 403.
   */
  function can(feature: FeatureKey, action: PermissionAction = 'view'): boolean {
    return granted.has(permissionKey(feature, action));
  }

  /** Verdadeiro se **qualquer** um dos pares estiver concedido (semântica OR). */
  function canAny(pairs: Array<[FeatureKey, PermissionAction]>): boolean {
    return pairs.some(([feature, action]) => can(feature, action));
  }

  return {
    can,
    canAny,
    role: data?.role ?? null,
    permissions: granted,
    isLoading,
    isError,
  };
}

export { FEATURES };
export type { FeatureKey, PermissionAction };
